import * as React from 'react';
import { useState } from 'react';
import { MapPin, Plus, User, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';

import {
  DROPDOWN_FETCH_PAGE_SIZE,
  formatPhoneNumber,
  RequirePermission,
  SEARCH_DEBOUNCE_MS,
  useDebouncedValue,
} from '@klareospace/pharos-lib-front-utils';
import { deduplicate, joinStrings } from '@klareospace/pharos-lib-utils';
import type { Customer, CustomerWithResolvedRefs } from '@klareospace/pharos-lib-types';
import { Button, cn, SearchInput } from '@klareospace/pharos-lib-web-utils';

import { CustomerDrawer, getCustomerDisplayName, useCustomers } from '&/features/customers';
import { useMasterContext } from '&/master';

import type { OrderCreationFormInput } from '../../schemas/order-creation.schema';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CustomerSectionProps {
  /** Clears the selected customer and returns the section to the search state. */
  onRemove: () => void;
  /** Attaches a customer (picked from search or just created) as the order's customer. */
  onSelect: (customer: Customer) => void;
  /** The customer currently attached to the order, or `null` while the section is incomplete. */
  selectedCustomer: Customer | null;
}

/** Pre-resolved (translated) display fields for one search result row. */
interface CustomerResultViewModel {
  addressSummary: string | null;
  contactLine: string;
  customer: CustomerWithResolvedRefs;
  displayName: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * "Customer" section body — search-and-attach an existing customer, or create one on the fly via
 * the standard `CustomerDrawer`. Once attached, the section shows a read-only summary card instead
 * (the drawer marks the section complete and auto-advances on selection).
 */
export function CustomerSection({ onRemove, onSelect, selectedCustomer }: CustomerSectionProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('order-creation');
  const { formState } = useFormContext<OrderCreationFormInput>();
  const { siteId, spaceId } = useMasterContext();
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const hasSearchQuery = debouncedSearch.trim().length > 0;
  const listboxIdPrefix = React.useId();
  const listboxId = `${listboxIdPrefix}-listbox`;

  const { data, isError, isLoading } = useCustomers({
    enabled: isCustomerSearchEnabled(selectedCustomer, hasSearchQuery),
    page: 1,
    pageSize: DROPDOWN_FETCH_PAGE_SIZE,
    search: debouncedSearch,
    siteId: siteId ?? undefined,
    spaceId: spaceId ?? undefined,
  });
  const results = data?.items ?? [];
  const { activeDescendantId, isResultsVisible } = resolveResultsAriaState({
    activeIndex,
    debouncedSearch,
    isLoading,
    listboxIdPrefix,
    results,
    search,
  });
  const requiredError = formState.errors.customer?.selectedCustomer?.message;

  /** "N addresses - city, city" from the customer's distinct address cities, or `null` with none. */
  function buildAddressSummary(customer: CustomerWithResolvedRefs): string | null {
    if (customer.addresses.length === 0) return null;
    const cities = deduplicate(
      customer.addresses
        .map((address) => address.location.address.city?.trim())
        .filter((city): city is string => !!city),
    );
    return t('sections.customer.fields.addressSummary', {
      cities: joinStrings(cities),
      count: customer.addresses.length,
    });
  }

  const resultViewModels: CustomerResultViewModel[] = results.map((customer) => ({
    addressSummary: buildAddressSummary(customer),
    contactLine: buildContactLine(customer),
    customer,
    displayName: getCustomerDisplayName(customer),
  }));

  function handleOpenCreateDrawer(): void {
    setIsCreateDrawerOpen(true);
  }

  function handleCustomerCreated(customer: Customer): void {
    setIsCreateDrawerOpen(false);
    onSelect(customer);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Escape') {
      setSearch('');
      setActiveIndex(-1);
      return;
    }
    // While results are hidden (debounce still settling, loading, or empty), keyboard nav
    // must not act on the stale/invisible `results` array — same guard as the mouse-click path.
    if (!isResultsVisible || results.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((previous) => Math.min(previous + 1, results.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((previous) => Math.max(previous - 1, 0));
    } else if (event.key === 'Enter') {
      const activeResult = activeIndex >= 0 && activeIndex < results.length ? results[activeIndex] : undefined;
      if (!activeResult) return;
      event.preventDefault();
      onSelect(activeResult);
    }
  }

  if (selectedCustomer) {
    return (
      <CustomerSummaryCard
        contactLine={buildContactLine(selectedCustomer)}
        displayName={getCustomerDisplayName(selectedCustomer)}
        onRemove={onRemove}
        removeLabel={t('sections.customer.fields.remove')}
      />
    );
  }

  return (
    <div className="space-y-3">
      <SearchInput
        ariaActivedescendant={activeDescendantId}
        ariaAutocomplete="list"
        ariaControls={listboxId}
        ariaExpanded={hasSearchQuery}
        ariaLabel={t('sections.customer.fields.searchAriaLabel')}
        isLoading={isLoading}
        onChange={(value): void => {
          setSearch(value);
          setActiveIndex(-1);
        }}
        onKeyDown={handleKeyDown}
        placeholder={t('sections.customer.fields.searchPlaceholder')}
        role="combobox"
        value={search}
      />
      {requiredError && <p className="text-sm text-destructive">{requiredError}</p>}
      {hasSearchQuery && (
        <CustomerDropdownPanel
          activeIndex={activeIndex}
          ariaLabel={t('sections.customer.fields.searchAriaLabel')}
          errorLabel={t('sections.customer.fields.searchError')}
          groupLabel={tCommon('navigation.customers')}
          isError={isError}
          isResultsVisible={isResultsVisible}
          listboxId={listboxId}
          listboxIdPrefix={listboxIdPrefix}
          newCustomerLabel={tCommon('fields.newCustomerTitle')}
          noResultsLabel={tCommon('address.emptyResults')}
          onOpenCreateDrawer={handleOpenCreateDrawer}
          onSelect={onSelect}
          results={resultViewModels}
        />
      )}
      <CustomerDrawer
        customer={null}
        onClose={(): void => setIsCreateDrawerOpen(false)}
        onCreated={handleCustomerCreated}
        open={isCreateDrawerOpen}
      />
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface NewCustomerRowProps {
  label: string;
  onClick: () => void;
}

function NewCustomerRow({ label, onClick }: NewCustomerRowProps): React.JSX.Element {
  return (
    <RequirePermission permission="customers:manage">
      <button
        className="mb-1.5 flex w-full items-center gap-2 rounded-md border border-primary/20 bg-primary/5 p-2.5 text-left hover:bg-primary/10"
        onClick={onClick}
        type="button"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Plus aria-hidden="true" className="h-3.5 w-3.5 text-primary" />
        </span>
        <span className="text-sm font-medium text-primary">{label}</span>
      </button>
    </RequirePermission>
  );
}

interface CustomerDropdownPanelProps {
  activeIndex: number;
  ariaLabel: string;
  errorLabel: string;
  groupLabel: string;
  isError: boolean;
  isResultsVisible: boolean;
  listboxId: string;
  listboxIdPrefix: string;
  newCustomerLabel: string;
  noResultsLabel: string;
  onOpenCreateDrawer: () => void;
  onSelect: (customer: Customer) => void;
  results: CustomerResultViewModel[];
}

/**
 * Autocomplete dropdown panel anchored below the search input. "New customer" is pinned as the
 * panel's first row so it stays reachable even while results are loading or empty/errored; the
 * results group below it only swaps once the debounced query has settled and isn't loading
 * (`isResultsVisible`) — this also prevents a stale (pre-edit) row from staying clickable.
 */
function CustomerDropdownPanel({
  activeIndex,
  ariaLabel,
  errorLabel,
  groupLabel,
  isError,
  isResultsVisible,
  listboxId,
  listboxIdPrefix,
  newCustomerLabel,
  noResultsLabel,
  onOpenCreateDrawer,
  onSelect,
  results,
}: CustomerDropdownPanelProps): React.JSX.Element {
  return (
    <div className="max-h-[320px] overflow-y-auto overscroll-contain rounded-md border bg-background p-1.5">
      <NewCustomerRow label={newCustomerLabel} onClick={onOpenCreateDrawer} />
      {isResultsVisible && (
        <CustomerDropdownResults
          activeIndex={activeIndex}
          ariaLabel={ariaLabel}
          errorLabel={errorLabel}
          groupLabel={groupLabel}
          isError={isError}
          listboxId={listboxId}
          listboxIdPrefix={listboxIdPrefix}
          noResultsLabel={noResultsLabel}
          onSelect={onSelect}
          results={results}
        />
      )}
    </div>
  );
}

interface CustomerDropdownResultsProps {
  activeIndex: number;
  ariaLabel: string;
  errorLabel: string;
  groupLabel: string;
  isError: boolean;
  listboxId: string;
  listboxIdPrefix: string;
  noResultsLabel: string;
  onSelect: (customer: Customer) => void;
  results: CustomerResultViewModel[];
}

function CustomerDropdownResults({
  activeIndex,
  ariaLabel,
  errorLabel,
  groupLabel,
  isError,
  listboxId,
  listboxIdPrefix,
  noResultsLabel,
  onSelect,
  results,
}: CustomerDropdownResultsProps): React.JSX.Element {
  if (isError) {
    return <p className="px-2.5 py-2 text-sm text-destructive">{errorLabel}</p>;
  }
  if (results.length === 0) {
    return <p className="px-2.5 py-2 text-sm text-muted-foreground">{noResultsLabel}</p>;
  }

  return (
    <div className="space-y-1">
      <span className="block px-2 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {groupLabel}
      </span>
      <ul aria-label={ariaLabel} id={listboxId} role="listbox">
        {results.map((result, index) => (
          <CustomerResultRow
            id={buildCustomerOptionId(listboxIdPrefix, result.customer.id)}
            isActive={index === activeIndex}
            key={result.customer.id}
            onSelect={onSelect}
            result={result}
          />
        ))}
      </ul>
    </div>
  );
}

interface CustomerResultRowProps {
  id: string;
  isActive: boolean;
  onSelect: (customer: Customer) => void;
  result: CustomerResultViewModel;
}

function CustomerResultRow({ id, isActive, onSelect, result }: CustomerResultRowProps): React.JSX.Element {
  const { addressSummary, contactLine, customer, displayName } = result;

  return (
    <li
      aria-selected={isActive}
      className={cn('flex w-full items-start gap-3 rounded-md p-2.5 hover:bg-muted/60', isActive && 'bg-muted/60')}
      id={id}
      onMouseDown={(event): void => {
        event.preventDefault();
        onSelect(customer);
      }}
      role="option"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10">
        <User aria-hidden="true" className="h-3.5 w-3.5 text-primary" />
      </span>
      <div className="min-w-0 flex-1 text-left">
        <p className="break-words text-sm font-medium">{displayName}</p>
        {contactLine && <p className="text-xs text-muted-foreground">{contactLine}</p>}
        {addressSummary && (
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin aria-hidden="true" className="h-3 w-3 shrink-0" />
            {addressSummary}
          </p>
        )}
      </div>
    </li>
  );
}

interface CustomerSummaryCardProps {
  contactLine: string;
  displayName: string;
  onRemove: () => void;
  removeLabel: string;
}

/** Read-only recap shown once a customer is attached — mirrors `DriverChip`'s card shape. */
function CustomerSummaryCard({
  contactLine,
  displayName,
  onRemove,
  removeLabel,
}: CustomerSummaryCardProps): React.JSX.Element {
  return (
    <div className="flex items-center gap-3 rounded-md border bg-background px-3 py-2">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
        <User aria-hidden="true" className="h-4 w-4 text-primary" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{displayName}</p>
        {contactLine && <p className="truncate text-xs text-muted-foreground">{contactLine}</p>}
      </div>
      <Button
        aria-label={removeLabel}
        className="h-7 w-7 shrink-0"
        onClick={onRemove}
        size="icon"
        type="button"
        variant="ghost"
      >
        <X aria-hidden="true" className="h-4 w-4" />
      </Button>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Only search once no customer is attached and the user has typed something. */
function isCustomerSearchEnabled(selectedCustomer: Customer | null, hasSearchQuery: boolean): boolean {
  return selectedCustomer === null && hasSearchQuery;
}

interface ResolveResultsAriaStateParams {
  activeIndex: number;
  debouncedSearch: string;
  isLoading: boolean;
  listboxIdPrefix: string;
  results: CustomerWithResolvedRefs[];
  search: string;
}

interface ResultsAriaState {
  activeDescendantId: string | undefined;
  isResultsVisible: boolean;
}

/**
 * Resolves whether the results group should render and, if so, the active option's DOM id.
 * Results stay hidden (`isResultsVisible: false`) while `search` runs ahead of `debouncedSearch`
 * (the user is still typing, within `SEARCH_DEBOUNCE_MS`) — this keeps a stale, pre-edit row from
 * staying clickable or keyboard-selectable until the debounced query actually settles.
 */
function resolveResultsAriaState(params: ResolveResultsAriaStateParams): ResultsAriaState {
  const { activeIndex, debouncedSearch, isLoading, listboxIdPrefix, results, search } = params;
  const isResultsVisible = search.trim() === debouncedSearch.trim() && !isLoading;
  if (!isResultsVisible || activeIndex < 0 || activeIndex >= results.length) {
    return { activeDescendantId: undefined, isResultsVisible };
  }
  return { activeDescendantId: buildCustomerOptionId(listboxIdPrefix, results[activeIndex].id), isResultsVisible };
}

/** Stable per-row id derived from the listbox's `useId` prefix, for `aria-activedescendant` wiring. */
function buildCustomerOptionId(listboxIdPrefix: string, customerId: string): string {
  return `${listboxIdPrefix}-option-${customerId}`;
}

/** "email · phone", dropping either side when absent. */
function buildContactLine(customer: Customer): string {
  const parts = [customer.email, formatPhoneNumber(customer.phone)].filter((part): part is string => !!part);
  return joinStrings(parts, ' · ');
}
